
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AnimatedCounter from "@/components/AnimatedCounter";
import { getAssetPath, tryMultipleAssetPaths } from "@/utils/assetUtils";
import PartnerLogos from "@/components/PartnerLogos";

const HeroSection = () => {
  const [assetsLoaded, setAssetsLoaded] = useState({
    background: false,
    ctaButton: false,
    heroGraphic: false
  });
  
  const [assetPaths, setAssetPaths] = useState({
    background: "",
    ctaButton: "",
    heroGraphic: ""
  });

  // Load assets and find working paths
  useEffect(() => {
    const loadAssets = async () => {
      // Try each asset path in sequence
      try {
        // Background
        tryMultipleAssetPaths("hero_graphic_background.png", (path) => {
          if (path) {
            console.log("✅ Successfully loaded background path:", path);
            setAssetPaths(prev => ({ ...prev, background: path }));
          }
        });
        
        // CTA Button
        tryMultipleAssetPaths("landing_become_a_driver_cta.png", (path) => {
          if (path) {
            console.log("✅ Successfully loaded CTA button path:", path);
            setAssetPaths(prev => ({ ...prev, ctaButton: path }));
          }
        });
        
        // Hero Graphic (replacing the trip card and earnings chart)
        tryMultipleAssetPaths("landing_hero_graphic.png", (path) => {
          if (path) {
            console.log("✅ Successfully loaded hero graphic path:", path);
            setAssetPaths(prev => ({ ...prev, heroGraphic: path }));
          }
        });
      } catch (error) {
        console.error("Error loading assets:", error);
      }
    };
    
    loadAssets();
  }, []);

  return (
    <>
      <div className="absolute inset-0 z-0 opacity-60 flex items-center justify-center" style={{ top: '-50%' }}>
        {!assetsLoaded.background && assetPaths.background === "" && (
          <div className="w-1/2 h-64 bg-green-500/20 rounded-full blur-3xl"></div>
        )}
        
        {assetPaths.background && (
          <img 
            src={assetPaths.background} 
            alt="Background Pattern" 
            className="w-1/2 h-auto object-contain"
            onLoad={() => setAssetsLoaded(prev => ({ ...prev, background: true }))}
            onError={(e) => {
              console.error("Failed to load background pattern", e);
            }}
          />
        )}
      </div>
      
      <section className="pt-44 pb-12 px-6 md:px-10 bg-transparent relative z-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h1 className="font-bold leading-tight mb-8">
              <span className="text-3xl sm:text-4xl md:text-[45px] md:leading-tight block">The Official Party<br />
              Bus Sourcing Partner<br />
              for Major Events</span>
            </h1>
            
            <div>
              <h3 className="text-xl mb-4">Trips you missed last week</h3>
              <AnimatedCounter />
            </div>
            
            <div className="mt-8">
              <Link to="/register-new" className="inline-block">
                {!assetsLoaded.ctaButton && assetPaths.ctaButton === "" && (
                  <button className="bg-green-500 text-black font-bold py-3 px-6 rounded hover:bg-green-400 transition-colors">
                    Become a Verified Driver
                  </button>
                )}
                
                {assetPaths.ctaButton && (
                  <img 
                    src={assetPaths.ctaButton} 
                    alt="Become a Verified Driver" 
                    className="h-12 hover:opacity-90 transition-opacity"
                    onLoad={() => setAssetsLoaded(prev => ({ ...prev, ctaButton: true }))}
                    onError={(e) => {
                      console.error("Failed to load CTA button", e);
                    }}
                  />
                )}
                <span className="sr-only">Become a Verified Driver</span>
              </Link>
            </div>
          </div>
          
          <div className="relative flex justify-center items-center">
            {/* Hero graphic replacing trip card and earnings chart */}
            <div className="w-full max-w-lg relative z-10">
              {!assetsLoaded.heroGraphic && assetPaths.heroGraphic === "" && (
                <div className="bg-white/10 rounded-xl overflow-hidden shadow-sm w-full h-96 animate-pulse">
                  <div className="h-full bg-gray-800/30"></div>
                </div>
              )}
              
              {assetPaths.heroGraphic && (
                <img 
                  src={assetPaths.heroGraphic} 
                  alt="Disco Driver Preview" 
                  className="w-full rounded-xl shadow-lg transition-all duration-500 hover:scale-105 scale-125"
                  onLoad={() => setAssetsLoaded(prev => ({ ...prev, heroGraphic: true }))}
                  onError={(e) => {
                    console.error("Failed to load hero graphic", e);
                  }}
                />
              )}
            </div>
          </div>
        </div>
        
        {/* Partner logos section */}
        <PartnerLogos />
      </section>
    </>
  );
};

export default HeroSection;
